import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { JoinTenant } from '@app/+state/access-requests';
import { TENANT_MGMT_ROUTES } from '@app/+state/user-data/user.model';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  Module,
  PaginationChangedEvent,
  ProcessCellForExportParams,
  RowSelectedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { CellSelectionModule, ClipboardModule } from 'ag-grid-enterprise';

import { sanitizedCellRenderer, toFilterModel } from '@app/v2/shared/utils';
import { AgToolbarGridComponent, RASymbols } from '@lemans/shared';
import { HomeGridColumnDefs as GridColumnDefs } from '@lemans/shared/grid/home-grid.utils';
import { OrganizationColumnRendererComponent } from '@lemans/shared/grid/ag-renderers/home-ui/ag-organization-column-renderer/organization-column-renderer.component';
import {
  AgFilterModel,
  ControlPageFacade,
  ITenantDetail,
  PageResponse,
  SortFilterContext,
  SortModel,
  UserDataFacade,
} from '@ra-state';
import { RaUiButtonModule } from '@ra-web-tech-ui-toolkit/components';
import { DataService } from '@servicesV2/data.service';
import { LoggerService } from '@servicesV2/logger.service';
import { Observable, Subject } from 'rxjs';
import { ViewTitleComponent } from '../../../shared/view-title/view-title.component';

@Component({
  selector: 'app-join-organization',
  standalone: true,
  imports: [CommonModule, ViewTitleComponent, AgToolbarGridComponent, RaUiButtonModule],
  styleUrls: ['./join-organization.component.scss'],
  templateUrl: './join-organization.component.html',
})
export class JoinOrganizationComponent implements OnInit {
  router = inject(Router);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly dataService = inject(DataService);
  private readonly logger = inject(LoggerService);
  sanitizer = inject(DomSanitizer);

  modules: Module[] = [CellSelectionModule, ClipboardModule, ClientSideRowModelModule, AllCommunityModule];
  cancelText = 'Cancel';
  continueText = 'Continue';
  headline = 'Welcome to FactoryTalk Hub!';
  title = 'Join Organization';
  currentTenantId$ = this.userDataFacade.currentTenantId$;
  isUserFound$ = this.controlPageFacade.isUserFound$;
  gridOptions: GridOptions;
  gridApi: GridApi | null | undefined;
  userData$ = this.userDataFacade.userData$;
  isOrgSelected: boolean = false;
  isGridReady$ = new Subject();
  pageRecords: number = 10;
  currentPageSize: number = 10;
  noRowsOverlayMessage: string = 'No organizations found';
  selectedTenant: ITenantDetail;

  getSuggestedTenants(): any {
    return {
      getRows: (_params): any => {
        const sortFilterContext: SortFilterContext = {
          sortModel: _params.request.sortModel as SortModel[],
          filterModel: toFilterModel(_params.request.filterModel as AgFilterModel),
        };
        this.getSuggestedTenants$(_params.request.endRow / this.pageRecords, sortFilterContext).subscribe((data) => {
          if (data.records.length === 0) {
            this.gridApi?.showNoRowsOverlay();
          } else {
            this.gridApi?.hideOverlay();
          }
          _params.success({
            rowCount: data.total,
            rowData: data.records.map((record) => {
              this.currentPageSize = this.currentPageSize + this.pageRecords;
              return record;
            }),
          });
        });
      },
    };
  }

  ngOnInit(): void {
    this.initializeAGGrid();
  }

  getSuggestedTenants$(pageNum: number, sortFilterContext: SortFilterContext): Observable<PageResponse<ITenantDetail>> {
    return this.dataService.getSuggestedTenants$(
      {
        pageNum: pageNum,
        perPage: this.pageRecords,
      },
      sortFilterContext,
    );
  }
  // Row selection handler
  onRowSelected(event: RowSelectedEvent): void {
    this.selectedTenant = event.data as ITenantDetail;
    this.isOrgSelected = true;
    this.gridApi?.getCellRendererInstances({ columns: ['name'] })?.forEach((cell: any) => {
      if (cell.params.data.id === event.data.id) {
        cell.clicked();
      }
    });
    this.logger.log('Selected tenant:', this.selectedTenant);
  }

  onSortChanged(_event: SortChangedEvent): void {
    this.resetOrganizationSelection();
  }

  onFilterChanged(_event: FilterChangedEvent): void {
    this.resetOrganizationSelection();
  }

  onPaginationChanged(_event: PaginationChangedEvent): void {
    this.resetOrganizationSelection();
  }

  resetOrganizationSelection(): void {
    this.isOrgSelected = false;
    this.gridApi?.deselectAll();
  }
  userNameEmailRenderer(params: ICellRendererParams): string {
    const name = params.data.ownerName || '';
    const email = params.data.ownerEmail || '';
    return `<div class='user-name-email'>
      <div>${name}</div>
      <div>${email}</div>
    </div>`;
  }

  columnDefs: ColDef[] = [
    GridColumnDefs.extend(GridColumnDefs.textCol('name', 'Organization'), {
      cellRenderer: OrganizationColumnRendererComponent,
      getQuickFilterText: (): string => '',
    }),
    GridColumnDefs.extend(GridColumnDefs.textCol('location', 'Location'), {
      minWidth: 100,
      valueFormatter: (params): string | RASymbols.MINUS => params.data.location || RASymbols.MINUS,
    }),
    GridColumnDefs.extend(GridColumnDefs.textCol('ownerName', 'Owner'), {
      cellRenderer: sanitizedCellRenderer(this.sanitizer, this, this.userNameEmailRenderer),
    }),
  ];

  initializeAGGrid(): void {
    const context = { componentParent: this };
    this.gridOptions = {
      context: context,
      rowSelection: { mode: 'singleRow', checkboxes: false, enableClickSelection: true },
    };
  }

  onGridReady(gridOptions: GridReadyEvent): void {
    this.gridApi = gridOptions.api;
    gridOptions.api?.setGridOption('loading', true);

    this.isGridReady$.next(true);
  }

  onFirstDataRendered(_event: FirstDataRenderedEvent): void {
    // select radio button for first row by default
    if (this.gridApi) {
      this.gridApi.getRenderedNodes()[0]?.setSelected(true);
      const orgNameRenders = this.gridApi?.getCellRendererInstances({ columns: ['name'] });
      if (orgNameRenders?.length) {
        const firstOrgRenderer = orgNameRenders[0] as any;
        firstOrgRenderer?.clicked();
        this.isOrgSelected = true;
      }
    }
  }

  joinOrg(): void {
    const joinTenant: JoinTenant = {
      requestBody: {
        resourceId: this.selectedTenant.id,
        tenantId: this.selectedTenant.id,
      },
      toastBody: {
        tenantName: this.selectedTenant.name,
      },
    };
    this.userDataFacade.accessRequests.joinTenant(joinTenant);
  }

  cancelJoinOrg(): void {
    this.router.navigate([TENANT_MGMT_ROUTES.ORGANIZATIONS]);
  }

  getInitials(fullName: string): string {
    return fullName
      ? fullName
          .split(' ')
          .map((nameList) => nameList[0])
          .join('')
      : '';
  }

  processCellForClipboard(params: ProcessCellForExportParams): any {
    if (params.node) {
      const columnName = params.column['colDef'].field;
      switch (columnName) {
        case 'organization': {
          const organizationName = params.node.data.name;
          const description = params.node.data.description;
          return organizationName.concat(description ? ': ' + description : '');
        }
        case 'location':
          return params.node.data.location || RASymbols.MINUS;
        default:
          return params.value;
      }
    }
    return params.value;
  }
}
